import React, { useState } from 'react';
import { Lock } from 'lucide-react';
import { Switch } from '../ui/Field';
import { pillPrimary, pillSecondary } from '../ui/pills';
import { messageErreur, type Sensible } from '../../lib/equipeApi';
import { useEquipe } from './EquipeShell';

/** Données sensibles d'une personne (fiche, onglet « Données sensibles ») : verrouillées par défaut ; autoriser demande une confirmation qui nomme la personne. */
export const DonneesSensiblesPersonne: React.FC<{ compteId: string; nom: string; sensibles: Sensible[]; onChange: () => void | Promise<void> }> = ({ compteId, nom, sensibles, onChange }) => {
    const { api } = useEquipe();
    const [attente, setAttente] = useState<string | null>(null);
    const [message, setMessage] = useState('');
    const prenom = nom.split(' ')[0];

    const fixer = async (categorie: string, accorde: boolean) => {
        setMessage('');
        try { await api.fixerSensible({ collaborator_id: compteId, categorie, accorde, confirmation_nom: accorde ? nom : undefined }); setAttente(null); await onChange(); }
        catch (e) { setMessage(messageErreur(e)); }
    };

    return (
        <div className="space-y-3">
            <p className="text-[13px] text-content-muted">Par défaut, {prenom} ne voit pas ces informations. Autorisez ce qui est utile à son poste : chaque changement est inscrit dans l'historique.</p>
            {message && <p role="alert" className="text-[13px] text-state-danger">{message}</p>}
            <div className="bg-surface border border-border rounded-xl overflow-hidden">
                {sensibles.map((s) => {
                    const enAttente = attente === s.categorie;
                    return (
                        <div key={s.categorie} className="px-5 py-3 border-b border-border-soft last:border-b-0">
                            <div className="flex items-center justify-between gap-3">
                                <div className="min-w-0"><span className="flex items-center gap-2 text-[13px] font-semibold text-content"><Lock size={13} /> {s.label}</span><span className="block text-[12px] text-content-muted">{s.description}</span></div>
                                {s.applique ? (
                                    <label className="flex items-center gap-2 text-[12.5px] text-content-muted shrink-0">{s.accorde ? 'Autorisé' : 'Verrouillé'}
                                        <Switch checked={s.accorde || enAttente} aria-label={`${s.label} — ${nom}`}
                                            onChange={(e) => { if (e.target.checked) setAttente(s.categorie); else if (enAttente) setAttente(null); else void fixer(s.categorie, false); }} />
                                    </label>
                                ) : <span className="text-[12px] text-content-muted shrink-0">Pas encore réglable</span>}
                            </div>
                            {enAttente && (
                                <div className="mt-3 flex flex-wrap items-center gap-3 rounded-xl bg-state-warn-soft text-state-warn px-4 py-3 text-[13px]">
                                    <span className="flex-1 min-w-[220px]">Autoriser <b>{nom}</b> à voir : {s.label.toLowerCase()} ?</span>
                                    <button type="button" className={`${pillSecondary(32)} max-md:!h-11`} onClick={() => setAttente(null)}>Annuler</button>
                                    <button type="button" className={`${pillPrimary(32)} max-md:!h-11`} onClick={() => fixer(s.categorie, true)}>Autoriser {prenom}</button>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
